import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface UsePortalReturn {
    PortalComponent: React.FC;
}

interface OnUnmountCallback {
    (): void;
}

// Custom hook that will only trigger its callback once - when the component unmounts
const useUnmount = (onUnmount: OnUnmountCallback) =>
    useEffect(() => onUnmount, []);

/**
 * For modals etc. that need to be rendered outside of the normal DOM hierarchy rendered by React.
 * Will find or create a portal element with the id 'rwl-portal-<idSuffix>'.  Returns a component
 * that can be used to render children into the portal element.  The portal element will be
 * removed from the DOM when the component unmounts.
 *
 * @param idSuffix - will be appended to 'rwl-portal-' to create the id of the portal element
 * @returns
 */
export function usePortal(idSuffix: string): UsePortalReturn {
    const portalElementId = `rwl-portal-${idSuffix}`;

    let portalContainer = document.querySelector(`#${portalElementId}`);
    if (!portalContainer) {
        portalContainer = document.createElement('div');
        portalContainer.id = portalElementId;
        portalContainer.setAttribute('data-testid', portalElementId);
        document.body.appendChild(portalContainer);
    }

    useUnmount(() => {
        portalContainer?.parentNode?.removeChild(portalContainer);
    });

    return {
        PortalComponent: ({ children }) => {
            if (!portalContainer) {
                return null;
            }
            return createPortal(children, portalContainer);
        },
    };
}
